import { AgentType, ConversationType, SessionType, SessionV2, jsonSafeToSessionV2 } from 'interfaces/db';
import { DEFAULT_LANGUAGE_SETTING as Language } from "./users";

export enum MessageType {
  HEARTBEAT = 'HEARTBEAT',
  USER_STATUS = 'USER_STATUS',
  SESSION_STATUS = 'SESSION_STATUS',
  SESSION_INVITE = 'SESSION_INVITE',
  LEADERBOARD_POSITION = 'LEADERBOARD_POSITION',
}

export type Message = Heartbeat | UserStatus | SessionStatus | SessionInvite | LeaderboardPosition

export interface UserStatus {
  message_type: MessageType.USER_STATUS
  user_id: string
  reachable: boolean
  active: boolean
  connected: boolean
  since_last_update: number | null
  twilio_client: string | null
}

export interface Heartbeat {
  message_type: MessageType.HEARTBEAT
  time: number
}

export interface SessionStatus {
  message_type: MessageType.SESSION_STATUS
  session: SessionV2
  metadata: ConversationMetadata
  worker_mono: number  // timestamp of unspecified per-worker origin
}

export interface SessionInvite {
  message_type: MessageType.SESSION_INVITE
  status: SessionStatus
  is_dispatched: boolean
  user_id: string | null
  team_id: string | null
}

export interface LeaderboardPosition {
  message_type: MessageType.LEADERBOARD_POSITION
  user_id: string
  room_id: string
  count: number
}

export function jsonSafeToSessionStatus(v: Record<string, any>): SessionStatus {
  return {
    message_type: MessageType.SESSION_STATUS,
    session: jsonSafeToSessionV2(v.session),
    metadata: jsonSafeToConversationMetadata(v.metadata),
    worker_mono: v.worker_mono,
  }
}

export function jsonSafeToMessage(v: Record<string, any>): Message {
  switch (v.message_type) {
    case MessageType.HEARTBEAT: return {
      message_type: MessageType.HEARTBEAT,
      time: v.number,
    }
    case MessageType.USER_STATUS: return {
      message_type: MessageType.USER_STATUS,
      user_id: v.user_id,
      reachable: v.reachable,
      active: v.active,
      connected: v.connected,
      since_last_update: v.since_last_update ?? null,
      twilio_client: v.twilio_client ?? null,
    }
    case MessageType.SESSION_STATUS: return jsonSafeToSessionStatus(v)
    case MessageType.SESSION_INVITE: return {
      message_type: MessageType.SESSION_INVITE,
      status: jsonSafeToSessionStatus(v.status),
      is_dispatched: v.is_dispatched,
      user_id: v.user_id ?? null,
      team_id: v.team_id ?? null,
    }
    case MessageType.LEADERBOARD_POSITION: return {
      message_type: MessageType.LEADERBOARD_POSITION,
      user_id: v.user_id,
      room_id: v.room_id,
      count: v.count,
    }
    default: throw new Error(`unexpected message_type from relay: ${JSON.stringify(v.message_type)}`)
  }
}

export interface ConversationMetadata {
  // required fields
  conversation_type: ConversationType
  connection_type: ConnectionType
  customer: BaseConnection
  agents: BaseConnection[]
  creator_agent_type: AgentType | null
  creator_agent_id: string | null
  // fields filled in after creation
  call_metadata: CallMetadata | null
  ended_at: number | null
  language: Language | null
}

export type CallMetadata = {
  call_type: SessionType | null
  // telephony
  customer_phone: string | null
  agent_phone: string | null
  is_inbound: boolean | null
  is_autodial: boolean | null
  parallel_lines: number | null
  is_customer_ended: boolean | null
  // privacy
  customer_recorded: boolean
  customer_streaming_transcribed: boolean
  customer_transcript_persisted: boolean
}

export function jsonSafeToConversationMetadata(v: Record<string, any>): ConversationMetadata {
  return {
    conversation_type: v.conversation_type,
    connection_type: v.connection_type,
    customer: jsonSafeToBaseConnection(v.customer),
    agents: v.agents.map(jsonSafeToBaseConnection),
    creator_agent_type: v.creator_agent_type ?? null,
    creator_agent_id: v.creator_agent_id ?? null,
    call_metadata: v.call_metadata ? jsonSafeToCallMetadata(v.call_metadata) : null,
    ended_at: v.ended_at ?? null,
    language: v.language ?? null,
  }
}

export function jsonSafeToCallMetadata(v: Record<string, any>): CallMetadata {
  return {
    call_type: v.call_type ?? null,
    customer_phone: v.customer_phone ?? null,
    agent_phone: v.agent_phone ?? null,
    is_inbound: v.is_inbound ?? null,
    is_autodial: v.is_autodial ?? null,
    parallel_lines: v.parallel_lines ?? null,
    is_customer_ended: v.is_customer_ended ?? null,
    customer_recorded: v.customer_recorded ?? false,
    customer_streaming_transcribed: v.customer_streaming_transcribed ?? false,
    customer_transcript_persisted: v.customer_transcript_persisted ?? false,
  }
}

export enum IdentityType {
  CUSTOMER = 'CUSTOMER',
  AGENT = 'AGENT',
}

export enum ConnectionType {
  EXTENSION_VOICE = 'EXTENSION_VOICE',
  TWILIO_VOICE = 'TWILIO_VOICE',
  TWILIO_MESSAGING = 'TWILIO_MESSAGING',
}

export type Identity = CustomerIdentity | AgentIdentity

export interface BaseIdentity {
  identity_type: IdentityType
}

export interface CustomerIdentity extends BaseIdentity {
  identity_type: IdentityType.CUSTOMER
  customer_name: string | null
  customer_company: string | null
  customer_title: string | null
}

export interface AgentIdentity extends BaseIdentity {
  identity_type: IdentityType.AGENT
  agent_type: AgentType | null
  agent_id: string | null
  agent_name: string | null
}

export interface BaseConnection {
  connection_id: string
  identity: Identity
  connection_type: ConnectionType
  invited_at: number | null
  removed_at: number | null
  joined_at: number | null
  left_at: number | null
}

export function jsonSafeToIdentity(v: Record<string, any>): Identity {
  switch (v.identity_type) {
    case IdentityType.CUSTOMER: return {
      identity_type: IdentityType.CUSTOMER,
      customer_name: v.customer_name ?? null,
      customer_company: v.customer_company ?? null,
      customer_title: v.customer_title ?? null,
    }
    case IdentityType.AGENT: return {
      identity_type: IdentityType.AGENT,
      agent_type: v.agent_type ?? null,
      agent_id: v.agent_id ?? null,
      agent_name: v.agent_name ?? null,
    }
    default: throw new Error(`unsupported connection identity type: ${JSON.stringify(v.identity_type)}`)
  }
}

export function jsonSafeToBaseConnection(v: Record<string, any>): BaseConnection {
  return {
    connection_id: v.connection_id,
    identity: jsonSafeToIdentity(v.identity),
    connection_type: v.connection_type,
    invited_at: v.invited_at ?? null,
    removed_at: v.removed_at ?? null,
    joined_at: v.joined_at ?? null,
    left_at: v.left_at ?? null,
  }
}

export function connectionIsConnected(connection?: BaseConnection | null): boolean {
    if (!connection) { return false }
    return connection.joined_at !== null && connection.left_at === null && connection.removed_at === null
}

export function identityIsUserId(identity: Identity, userId: string): boolean {
    return identity.identity_type === IdentityType.AGENT
        && identity.agent_type === AgentType.USER
        && identity.agent_id === userId
}
